import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import socket from "../../socket";
import { format } from "date-fns";
import { toast } from "react-toastify";

import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Button,
  Tabs,
  Tab,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";

import AddNewService from "./AddNewService";
import UniversalDetailsPopup from "../../component/ui/UniversalDetailsPopup";
import { columnDefs } from "./serviceColumns";

import "../ServiceTracker/ServiceTrackerTable.css";

/* ================= HELPERS ================= */

const formatDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return "—";
  return format(dt, "dd-MM-yyyy");
};

const DEFAULT_COL_WIDTH = 180;

const getInitialWidths = () =>
  columnDefs.reduce((acc, c) => {
    acc[c.key] = DEFAULT_COL_WIDTH;
    return acc;
  }, {});

/* ================= COMPONENT ================= */

export default function ServiceTrackerParent() {
  /* ================= STATE ================= */

  const [rows, setRows] = useState([]);

  const [activeTab, setActiveTab] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [showAddPopup, setShowAddPopup] = useState(false);

  const [limit, setLimit] = useState(
    Number(localStorage.getItem("itemsPerPage")) || 10
  );
  const [totalPages, setTotalPages] = useState(1);

  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState({
    userName: "",
    projectName: "",
    customerName: "",
    modelNumber: "",
    location: "",
    serviceMode: "",
    warranty: "",
    serviceType: "",
  });

  const filterTimeout = useRef(null);

  const [bulkSelected, setBulkSelected] = useState([]);

  const [selectedRow, setSelectedRow] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [columnWidths, setColumnWidths] = useState(getInitialWidths);
  const [totalRecords, setTotalRecords] = useState(0);

  /* ================= COLUMN RESIZE ================= */

  const [sortConfig, setSortConfig] = useState([]);

  const handleSortClick = (columnKey, event) => {
    setSortConfig((prev) => {
      const safePrev = Array.isArray(prev) ? prev : []; // 🔐 SAFETY
      const isShift = event.shiftKey;

      let next = isShift ? [...safePrev] : [];

      const index = safePrev.findIndex(
        (s) => s && s.key === columnKey // 🔐 SAFETY
      );

      // 1️⃣ Not sorted → ASC
      if (index === -1) {
        next.push({ key: columnKey, direction: "asc" });
        return next;
      }

      // 2️⃣ ASC → DESC
      if (safePrev[index].direction === "asc") {
        next[index] = { key: columnKey, direction: "desc" };
        return next;
      }

      // 3️⃣ DESC → REMOVE
      next.splice(index, 1);
      return next;
    });
  };

  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();

    const startX = e.clientX;
    const startWidth = columnWidths[key];

    const onMouseMove = (ev) => {
      const diff = ev.clientX - startX;
      setColumnWidths((prev) => ({
        ...prev,
        [key]: Math.max(120, startWidth + diff),
      }));
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  /* ================= FETCH DATA ================= */

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}show-service`,
        {
          params: {
            ...filters,
            page: currentPage,
            limit,
            closeStatus: activeTab === 0 ? "open" : "closed",

            // ✅ ALWAYS send array (Excel-style)
            sort: JSON.stringify(sortConfig),
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      console.log("Sorted services:", res.data?.services);

      setRows(res.data?.services || []);
      setTotalPages(res.data?.totalPages || 1);
      setTotalRecords(res.data?.totalRecords || 0);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load services");
    }
  }, [filters, currentPage, limit, activeTab, sortConfig]);

  const startItem = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endItem = Math.min(currentPage * limit, totalRecords);

  console.log(totalRecords);
  useEffect(() => {
    fetchData();
    socket.on("update_data", fetchData);
    return () => socket.off("update_data", fetchData);
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(1);
    setBulkSelected([]);
  }, [filters, activeTab, limit]);

  /* ================= FILTER HANDLERS ================= */

  const handleFilterChange = (e) => {
    const { name, value } = e.target;

    clearTimeout(filterTimeout.current);
    filterTimeout.current = setTimeout(() => {
      setFilters((p) => ({ ...p, [name]: value }));
    }, 300);
  };

  const resetFilters = () => {
    setFilters({
      userName: "",
      projectName: "",
      customerName: "",
      modelNumber: "",
      location: "",
      serviceMode: "",
      warranty: "",
      serviceType: "",
    });
  };

  /* ================= SELECTION ================= */

  const toggleSelect = (id) => {
    setBulkSelected((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );
  };

  const toggleSelectAll = () => {
    if (!rows.length) return;

    setBulkSelected(
      bulkSelected.length === rows.length ? [] : rows.map((r) => r._id)
    );
  };

  const bulkDelete = async () => {
    if (!bulkSelected.length) return;
    if (!window.confirm("Delete selected services?")) return;

    try {
      await Promise.all(
        bulkSelected.map((id) =>
          axios.delete(`${process.env.REACT_APP_API_URL}delete-service/${id}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("jwt")}`,
            },
          })
        )
      );

      toast.success("Deleted");
      setBulkSelected([]);
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ================= DETAILS HANDLERS ================= */

  const openDetails = (row) => {
  const normalizedRow = {
    ...row,

    // ✅ normalize Warranty
    Warranty:
      row.Warranty === "In warranty" || row.Warranty === "in warranty"
        ? "In Warranty"
        : row.Warranty || "",

    // ✅ normalize CloseDate
    CloseDate:
      row.CloseDate && row.CloseDate !== ""
        ? row.CloseDate
        : null,

    // ✅ normalize CloseComment (CRITICAL)
    CloseComment:
      typeof row.CloseComment === "string"
        ? row.CloseComment
        : "",
  };

  setSelectedRow(normalizedRow);
  setDetailsOpen(true);
};


  const closeDetails = () => {
    setSelectedRow(null);
    setDetailsOpen(false);
  };

  const saveDetails = async (data) => {
    try {
      const res = await axios.put(
        `${process.env.REACT_APP_API_URL}update-service/${data._id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      const updated = res.data;

      setRows((prev) => prev.map((r) => (r._id === updated._id ? updated : r)));

      toast.success("Updated successfully");
      closeDetails();
      fetchData();
    } catch {
      toast.error("Update failed");
    }
  };

  const deleteDetails = async (id) => {
    if (!window.confirm("Delete this service?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}delete-service/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("jwt")}`,
          },
        }
      );

      toast.success("Deleted");
      closeDetails();
      fetchData();
    } catch {
      toast.error("Delete failed");
    }
  };

  /* ================= FIELD CONFIG ================= */

  const fieldTypes = {
    OpenDate: "date",
    CloseDate: "date",
    Comment: "textarea",
    CloseComment: "textarea",
    Specifications: "textarea",
    ServiceMode: {
      type: "select",
      options: ["Technical Call", "Site Visit", "In House"],
    },
    Warranty: {
      type: "select",
      options: ["In Warranty", "Expired"],
    },
    ServiceType: {
      type: "select",
      options: ["Paid Service", "Free Service", "To Be Discussed"],
    },
  };

  /* ================= RENDER ================= */

  return (
    <div className="show-service-wrap">
      {/* TOP BAR */}

      <div className="service-top-buttons">
        <Button variant="contained" onClick={() => setShowAddPopup(true)}>
          + ADD SERVICE
        </Button>

        <Button onClick={() => setFiltersOpen((p) => !p)}>
          {filtersOpen ? "HIDE FILTERS" : "SHOW FILTERS"}
        </Button>

        <Button
          variant="contained"
          color="secondary"
          disabled={!bulkSelected.length}
          onClick={bulkDelete}
        >
          DELETE SELECTED
        </Button>
      </div>
      {/* FILTERS */}
      <div className={`filters-wrapper ${filtersOpen ? "open" : "closed"}`}>
        <div className="filters-content">
          <TextField
            label="User Name"
            name="userName"
            onChange={handleFilterChange}
            fullWidth
          />
          <TextField
            label="Project Name"
            name="projectName"
            onChange={handleFilterChange}
            fullWidth
          />
          <TextField
            label="Customer Name"
            name="customerName"
            onChange={handleFilterChange}
            fullWidth
          />

          <TextField
            label="Location"
            name="location"
            onChange={handleFilterChange}
            fullWidth
          />

          <FormControl fullWidth size="small">
            <InputLabel id="service-mode-label">Service Mode</InputLabel>
            <Select
              labelId="service-mode-label"
              name="serviceMode"
              onChange={handleFilterChange}
              sx={{ height: 55 }}
              value={filters.serviceMode || ""}
              label="Service Mode"
            >
              <MenuItem value="">All Service Modes</MenuItem>
              <MenuItem value="Technical Call">Technical Call</MenuItem>
              <MenuItem value="Site Visit">Site Visit</MenuItem>
              <MenuItem value="In House">In House</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Model Number"
            name="modelNumber"
            onChange={handleFilterChange}
            fullWidth
          />

          <FormControl fullWidth size="small">
            <InputLabel id="warranty-label">Warranty</InputLabel>
            <Select
              labelId="warranty-label"
              name="warranty"
              sx={{ height: 55 }}
              onChange={handleFilterChange}
              value={filters.warranty || ""}
              label="Warranty"
            >
              <MenuItem value="">All Warranty</MenuItem>
              <MenuItem value="In Warranty">In Warranty</MenuItem>
              <MenuItem value="Expired">Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel id="service-type-label">Service Type</InputLabel>
            <Select
              labelId="service-type-label"
              name="serviceType"
              sx={{ height: 55 }}
              onChange={handleFilterChange}
              value={filters.serviceType || ""}
              label="Service Type"
            >
              <MenuItem value="">All Service Types</MenuItem>
              <MenuItem value="Paid Service">Paid Service</MenuItem>
              <MenuItem value="Free Service">Free Service</MenuItem>
              <MenuItem value="To Be Discussed">To Be Discussed</MenuItem>
            </Select>
          </FormControl>

          <div className="filter-actions">
            <Button variant="outlined" onClick={resetFilters}>
              Reset Filters
            </Button>
          </div>
        </div>
      </div>

      <div className="ServiceTableBox">
        {/* TABS */}
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          className={filtersOpen ? "with-filters" : ""}
        >
          <Tab label="OPEN SERVICES" />
          <Tab label="CLOSED SERVICES" />
        </Tabs>
      </div>

      {/* RANGE + PAGINATION */}
      <div className="service-range-box">
        <div>
          <label>Items per page:</label>
          <Select
            size="small"
            value={limit}
            onChange={(e) => {
              const v = Number(e.target.value);
              setLimit(v);
              localStorage.setItem("itemsPerPage", v);
              setCurrentPage(1);
            }}
            sx={{ ml: 1, width: 80 }}
          >
            {[10, 20, 30, 40, 50].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="pagination-controls">
          <Button
            size="small"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Prev
          </Button>

          <span style={{ padding: "0 10px" }}>
            Page {currentPage} of {totalPages}
          </span>

          <Button
            size="small"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <div className="range-text">
        Showing {startItem}–{endItem} of {totalRecords}
      </div>

      {/* TABLE */}
      <TableContainer component={Paper}>
        <Table sx={{ tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell
                onClick={toggleSelectAll}
                sx={{ width: 50, fontWeight: 700 }}
              >
                #
              </TableCell>

              {columnDefs.map((col) => (
                <TableCell
                  key={col.key}
                  onClick={(e) => handleSortClick(col.key, e)}
                  sx={{
                    width: columnWidths[col.key],
                    fontWeight: 600,
                    position: "relative",
                    whiteSpace: "nowrap",
                    overflow: "hidden", // ✅ IMPORTANT
                  }}
                >
                  {col.label}
                  {(() => {
                    const safeSort = Array.isArray(sortConfig)
                      ? sortConfig
                      : [];
                    const idx = safeSort.findIndex(
                      (s) => s && s.key === col.key
                    );

                    if (idx === -1) return null;

                    return (
                      <span style={{ marginLeft: 6, fontSize: 12 }}>
                        {safeSort[idx].direction === "asc" ? "▲" : "▼"}
                        {safeSort.length > 1 && <sup>{idx + 1}</sup>}
                      </span>
                    );
                  })()}

                  {/* COLUMN RESIZER */}
                  <div
                    onMouseDown={(e) => startResize(e, col.key)}
                    style={{
                      position: "absolute",
                      top: 0,
                      right: 0,
                      width: 8,
                      height: "100%",
                      cursor: "col-resize",
                      zIndex: 10,
                      pointerEvents: "auto", // ✅ IMPORTANT
                      background: "transparent",
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row, idx) => (
              <TableRow
                key={row._id}
                hover
                selected={bulkSelected.includes(row._id)}
                onClick={() => openDetails(row)}
                sx={{
                  cursor: "pointer",
                  height: 52,
                  maxHeight: 52, // ✅ LOCK HEIGHT
                }}
              >
                <TableCell
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleSelect(row._id);
                  }}
                  sx={{
                    height: 52,
                    maxHeight: 52,
                    overflow: "hidden",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    verticalAlign: "middle",
                  }}
                >
                  {(currentPage - 1) * limit + idx + 1}
                </TableCell>

                {columnDefs.map((col) => {
                  const value =
                    col.key === "OpenDate" || col.key === "CloseDate"
                      ? formatDate(row[col.key])
                      : row[col.key] || "—";

                  const isComment =
                    col.key === "Comment" || col.key === "CloseComment";

                  return (
                    <TableCell
                      key={col.key}
                      sx={{
                        height: 52,
                        maxHeight: 52,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        verticalAlign: "middle",
                      }}
                    >
                      {isComment ? (
                        <Tooltip
                          title={
                            <div
                              style={{
                                maxWidth: 420,
                                maxHeight: 220,
                                overflowY: "auto",
                                whiteSpace: "pre-wrap",
                                lineHeight: 1.5,
                                fontSize: 13,
                                padding: 4,
                              }}
                            >
                              {value || "—"}
                            </div>
                          }
                          arrow
                          placement="right"
                        >
                          <span
                            style={{
                              display: "block",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                            }}
                          >
                            {value}
                          </span>
                        </Tooltip>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <div className="pagination-controls">
        <Button
          size="small"
          disabled={currentPage === 1}
          onClick={() => setCurrentPage((p) => p - 1)}
        >
          Prev
        </Button>

        <span>
          Page {currentPage} of {totalPages}
        </span>

        <TextField
          size="small"
          type="number"
          inputProps={{
            min: 1,
            max: totalPages,
          }}
          placeholder="Go"
          sx={{ width: 70 }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const page = Number(e.target.value);
              if (page >= 1 && page <= totalPages) {
                setCurrentPage(page);
              }
            }
          }}
        />

        <Button
          size="small"
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage((p) => p + 1)}
        >
          Next
        </Button>
      </div>

      {/* ADD SERVICE */}
      {showAddPopup && (
        <div className="service-popup-overlay">
          <div className="service-popup-content">
            <AddNewService onClose={() => setShowAddPopup(false)} />
          </div>
        </div>
      )}

      {/* DETAILS POPUP – FULLY WIRED */}
      <UniversalDetailsPopup
        open={detailsOpen}
        row={selectedRow}
        onClose={closeDetails}
        onSave={saveDetails}
        onDelete={deleteDetails}
        hiddenFields={["__v", "CreatedBy", "createdAt", "updatedAt", "_id"]}
        nonEditableFields={["UserName"]}
        fieldTypes={fieldTypes}
        fieldLabels={{
          ProjectName: "Project Name",
          CustomerName: "Customer Name",
          Location: "Location",
          ServiceMode: "Service Mode",
          ModelNumber: "Model Number",
          Issue: "Issue",
          Warranty: "Warranty",
          ServiceType: "Service Type",
          Specifications: "Specifications",
          Comment: "Comment",
          CloseComment: "Close Comment",
          OpenDate: "Opening Date",
          CloseDate: "Closing Date",
        }}
      />
    </div>
  );
}
